import React, { useEffect, useRef, useState } from "react";
import { Lottie } from "lottie-react";

/**
 * Universal cosmetic renderer: <img> for raster/svg/gif/webp,
 * <Lottie> for .json files. Falls back silently on load error.
 *
 * Caches parsed Lottie JSON globally so same asset isn't re-fetched.
 */

const _lottieCache = new Map();

async function loadLottie(url) {
  if (_lottieCache.has(url)) return _lottieCache.get(url);
  const promise = fetch(url)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  _lottieCache.set(url, promise);
  return promise;
}

export default function CosmeticAsset({
  asset,
  alt = "",
  className = "",
  style,
  onLoad,
  loop = true,
  autoplay = true,
  draggable = false,
  pointerEvents = true,
}) {
  const isJson = typeof asset === "string" && asset.toLowerCase().endsWith(".json");
  const [animData, setAnimData] = useState(null);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isJson) return;
    setAnimData(null);
    setFailed(false);
    loadLottie(asset).then((data) => {
      if (!mounted.current) return;
      if (data) {
        setAnimData(data);
        onLoad?.();
      } else {
        setFailed(true);
      }
    });
  }, [asset, isJson, onLoad]);

  const combinedStyle = {
    ...(style || {}),
    pointerEvents: pointerEvents ? style?.pointerEvents : "none",
  };

  if (!isJson) {
    return (
      <img
        src={asset}
        alt={alt}
        className={className}
        style={combinedStyle}
        draggable={draggable}
        onLoad={onLoad}
      />
    );
  }

  if (failed) {
    // Lottie load failed — render nothing (silent fallback)
    return <div className={className} style={combinedStyle} aria-label={alt} />;
  }

  if (!animData) {
    // Loading placeholder (keep layout stable)
    return <div className={className} style={combinedStyle} aria-label={alt} />;
  }

  return (
    <div className={className} style={combinedStyle} aria-label={alt}>
      <Lottie
        src={animData}
        loop={loop}
        autoplay={autoplay}
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}
